import { useState, useEffect, useRef, memo } from "react";
import { connect } from "react-redux";
import { saveAs } from "file-saver";
import Modal from "react-modal";
import { toggleLoading, setAlert } from "../../state/creators";
import DocViewer from "@cyntler/react-doc-viewer";
import "@cyntler/react-doc-viewer/dist/index.css";
import { ButtonList, ExpandCtaAnchor, ExportCtaAnchor } from "../../styles/type/buttons";
import { DocumentInterface } from "../../_types/document";
import { IHttpResponse } from "../../functions/http-functions/http";
import { ApiResultModel } from "../../_models/api.result.model";
import { AlertType } from "../../state/reducers/setAlert";
import { GetCandidateDocument, DeleteIndividualFile } from "../../functions/http-functions/documents";
import { useAuth } from "../../state/context/auth.store";
import { Loader } from "../../styles/loading";
import { PreviewWrapper, DeletePageButton, ShowDocumentButton } from "../../styles/documents/preview";
import { Note } from "../../styles/forms/inputs";
import { ModalCycleButton, ModalStyles, ModalToggleButtons, ModalCycleInfo } from "../../styles/modals";
import ConfirmModal from "../modals/confirmModal";
import DocViewerErrorBoundary from "../boundary/docViewerError";
import { isPasswordProtectedPdf, usePasswordProtectedPdfPrompt } from "../../hooks/usePasswordProtectedPdf";

type Props = {
	closeModal: () => void;
	document: DocumentInterface;
	toggleLoading: (loading: boolean) => void;
	setAlert: (alert: AlertType) => void;
	toggleFullScreen?: () => void;
};

const FilePreview = ({ closeModal, document, toggleLoading, setAlert, toggleFullScreen }: Props) => {
	const { auth } = useAuth();

	const [fileUrl, setFileUrl] = useState<string>(null);
	const [fileType, setFileType] = useState<string>(null);
	const [fileName, setFileName] = useState<string>(null);
	const [loading, setLoading] = useState<boolean>(true);
	const [currentPage, setCurrentPage] = useState<number>(0);
	const [fileUrls, setFileUrls] = useState<string[]>([]);
	const [fileBlobs, setFileBlobs] = useState<Blob[]>([]);
	const [isPasswordProtectedDocument, setIsPasswordProtectedDocument] = useState<boolean>(false);
	const [hasRevealedPasswordProtectedDocument, setHasRevealedPasswordProtectedDocument] = useState<boolean>(false);
	const [totalPages, setTotalPages] = useState<number>(1);
	const [confirmDeleteModalOpen, setConfirmDeleteModalOpen] = useState<boolean>(false);
	const [allFiles, setAllFiles] = useState<Array<{ reference: string; name: string; fileType: string }>>([]);
	// Ref to track blob URLs for cleanup - ensures we always have the latest URLs
	const fileUrlsRef = useRef<string[]>([]);

	useEffect(() => {
		if (document) {
			getDocument();
		} else {
			setLoading(false);
		}

		// Cleanup function: Revoke all blob URLs to prevent memory leaks
		// This runs when:
		// 1. Component unmounts (modal closes)
		// 2. Document changes (before loading new document)
		return () => {
			// Revoke all blob URLs that were created for this document
			// Use ref to ensure we have the latest URLs even if state hasn't updated yet
			fileUrlsRef.current.forEach((url) => {
				if (url) {
					URL.revokeObjectURL(url);
				}
			});
			// Clear the ref
			fileUrlsRef.current = [];
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [document]);

	useEffect(() => {
		// Update current file display when page changes
		if (fileUrls[currentPage] && allFiles.length > 0) {
			const currentFileName = allFiles[currentPage]?.name || "Unknown";
			setFileUrl(fileUrls[currentPage]);
			setFileName(currentFileName); // Update fileName to show current file name
		}
	}, [currentPage, fileUrls, fileBlobs, allFiles, totalPages]);

	usePasswordProtectedPdfPrompt(hasRevealedPasswordProtectedDocument, () => setHasRevealedPasswordProtectedDocument(false));

	const getDocument = async () => {
		try {
			setLoading(true);
			setCurrentPage(0);
			setHasRevealedPasswordProtectedDocument(false);

			// Clean up any existing blob URLs before creating new ones
			// This prevents memory leaks when switching between documents
			fileUrlsRef.current.forEach((url) => {
				if (url) {
					URL.revokeObjectURL(url);
				}
			});
			fileUrlsRef.current = [];

			// Build list of all files for multi-page document display
			// Document structure:
			// - Main file: stored directly on document (reference, name, fileType)
			// - Additional files: stored in document.files array with order field (1, 2, 3, etc.)
			// The main file is implicitly order 0, additional files start at order 1
			// Sort additional files by order field to ensure correct display order
			const sortedAdditionalFiles = (document.files || []).sort((a, b) => a.order - b.order);
			const filesArray = [
				// Main file (order 0) - always first
				{
					reference: document.reference,
					name: document.name,
					fileType: document.fileType,
				},
				// Additional files (order 1, 2, 3, etc.)
				...sortedAdditionalFiles,
			];

			setAllFiles(filesArray);
			setTotalPages(filesArray.length);
			setFileName(document.name);

			const type = document.name.split(".").pop();
			setFileType(type ? type.toLowerCase() : "");

			// Load all files
			const urls: string[] = [];
			const blobs: Blob[] = [];
			let isProtectedDocument = false;

			for (let i = 0; i < filesArray.length; i++) {
				const file = filesArray[i];
				try {
					const res = await GetCandidateDocument(auth.token, file.reference, document.type);
					const blob = new Blob([res.data], { type: file.fileType });
					blobs.push(blob);

					// Only check the first page of password-protected docs, since it's not
					// possible to password protect single pages
					if (i === 0) {
						isProtectedDocument = await isPasswordProtectedPdf(blob, file.name);
					}

					const fileType = file.name.split(".").pop();
					if (fileType && validFileTypes.includes(fileType.toLowerCase())) {
						const url = URL.createObjectURL(blob);
						urls.push(url);
					} else {
						urls.push(null);
					}
				} catch (fileErr) {
					urls.push(null);
					blobs.push(null);
				}
			}

			setFileUrls(urls);
			setFileBlobs(blobs);
			setIsPasswordProtectedDocument(isProtectedDocument);
			setFileUrl(urls[0]);
			// Update ref with new URLs for cleanup tracking
			fileUrlsRef.current = urls;
		} catch (err) {
			const error = err as IHttpResponse;
			const ErrorResult = new ApiResultModel(error);

			setAlert({ message: ErrorResult.message, type: "error" });
		} finally {
			setLoading(false);
		}
	};

	// Fallback download link if file type is not supported
	const renderFallback = () => (
		<div>
			<p>File type not supported for preview. Please download the file to view.</p>
			<p>We are working on enabling previews for as many file types as possible.</p>
			<ButtonList justify="center">
				<ExportCtaAnchor onClick={downloadDocument}>Download</ExportCtaAnchor>
			</ButtonList>
		</div>
	);

	const downloadDocument = () => {
		try {
			toggleLoading(true);
			const currentBlob = fileBlobs[currentPage];
			const FILE_NAME = allFiles[currentPage]?.name || document.name;
			if (window.navigator && window.navigator.msSaveBlob) {
				window.navigator.msSaveBlob(currentBlob, FILE_NAME);
			} else {
				saveAs(currentBlob, FILE_NAME);
			}
		} catch (err) {
			const error = err as IHttpResponse;
			const ErrorResult = new ApiResultModel(error);

			setAlert({ message: ErrorResult.message, type: "error" });
		} finally {
			toggleLoading(false);
		}
	};

	const downloadAll = () => {
		try {
			toggleLoading(true);
			fileBlobs.forEach((blob, index) => {
				if (blob) {
					const FILE_NAME = allFiles[index]?.name || `${document.name}-${index + 1}`;
					if (window.navigator && window.navigator.msSaveBlob) {
						window.navigator.msSaveBlob(blob, FILE_NAME);
					} else {
						saveAs(blob, FILE_NAME);
					}
				}
			});
		} catch (err) {
			const error = err as IHttpResponse;
			const ErrorResult = new ApiResultModel(error);

			setAlert({ message: ErrorResult.message, type: "error" });
		} finally {
			toggleLoading(false);
		}
	};

	const goToNextPage = () => {
		if (currentPage < totalPages - 1) {
			const nextPage = currentPage + 1;
			setCurrentPage(nextPage);
		}
	};

	const goToPreviousPage = () => {
		if (currentPage > 0) {
			const prevPage = currentPage - 1;
			setCurrentPage(prevPage);
		}
	};

	const handleDeletePageClick = () => {
		// Open confirmation modal
		setConfirmDeleteModalOpen(true);
	};

	const revealProtectedDocument = () => {
		setHasRevealedPasswordProtectedDocument(true);
	};

	const deleteCurrentPage = async () => {
		try {
			toggleLoading(true);
			setConfirmDeleteModalOpen(false);

			// Get the file to delete from allFiles array
			// allFiles structure: [mainFile (index 0), file1 (index 1), file2 (index 2), ...]
			// - currentPage 0 = main file (document.reference) - cannot be deleted
			// - currentPage 1+ = additional file from document.files array
			// Note: The delete button is only shown for currentPage > 0 (not the main file)
			const fileToDelete = allFiles[currentPage];

			if (!fileToDelete) {
				setAlert({ message: "File not found.", type: "error" });
				return;
			}

			await DeleteIndividualFile(auth.token, document._id, fileToDelete.reference);

			setAlert({ message: "File deleted successfully." });

			// Reload the document to refresh the file list
			window.location.reload(); // Simple reload for now
		} catch (err) {
			const error = err as IHttpResponse;
			const ErrorResult = new ApiResultModel(error);
			setAlert({ message: ErrorResult.message, type: "error" });
		} finally {
			toggleLoading(false);
		}
	};

	return (
		<PreviewWrapper>
			{loading ? (
				<div style={{ display: "flex", justifyContent: "center" }}>
					<Loader />
				</div>
			) : (
				<>
					{fileUrl && fileType ? (
						<>
							<Note>
								Below, you will find a preview of your document. You can inspect the file using the preview shown below. You may also download the document to your local machine by
								clicking &apos;Download File&apos; located beneath the preview.
							</Note>
							{/* Page Navigation - Top */}
							{totalPages > 1 && (
								<>
									<ModalToggleButtons>
										<ModalCycleButton direction="previous" onClick={goToPreviousPage} disabled={currentPage === 0}>
											Previous
										</ModalCycleButton>
										<ModalCycleInfo>
											Page {currentPage + 1} of {totalPages} - {allFiles[currentPage]?.name || "Unknown file"}
										</ModalCycleInfo>
										<ModalCycleButton direction="next" onClick={goToNextPage} disabled={currentPage === (totalPages || 1) - 1}>
											Next
										</ModalCycleButton>
									</ModalToggleButtons>
									{currentPage > 0 && (
										<ButtonList justify="flex-end" pushBottom>
											<DeletePageButton onClick={handleDeletePageClick}>Delete This Page</DeletePageButton>
										</ButtonList>
									)}
								</>
							)}
							{toggleFullScreen ? (
								<>
									<ButtonList justify="flex-end">
										<ExpandCtaAnchor style={{ marginTop: 0 }} flush={true} onClick={toggleFullScreen}>
											Click here for Full Screen Preview
										</ExpandCtaAnchor>
									</ButtonList>
								</>
							) : null}
							{isPasswordProtectedDocument && !hasRevealedPasswordProtectedDocument ? (
								<div>
									<Note pushBottom={true}>This document is password protected. Click below to enter the password to preview the file.</Note>
									<ButtonList justify="center" pushTop pushBottom>
										<ShowDocumentButton type="button" onClick={revealProtectedDocument}>
											Show document
										</ShowDocumentButton>
									</ButtonList>
								</div>
							) : null}
							{!isPasswordProtectedDocument || hasRevealedPasswordProtectedDocument ? (
								<DocViewerErrorBoundary>
									<DocViewer
										documents={[{ uri: fileUrl, fileName: fileName }]}
										theme={{
											primary: "#ffffff",
											secondary: "#ffffff",
											textPrimary: "#294338",
											textSecondary: "#294338",
											disableThemeScrollbar: false,
										}}
									/>
								</DocViewerErrorBoundary>
							) : null}
							<ButtonList justify="center" pushTop pushBottom>
								<ExportCtaAnchor onClick={downloadDocument}>{totalPages > 1 ? "Download Current Page" : "Download File"}</ExportCtaAnchor>
								{totalPages > 1 && <ExportCtaAnchor onClick={downloadAll}>Download All Pages</ExportCtaAnchor>}
							</ButtonList>
							<Note>
								Please be aware that by downloading documents, you assume responsibility for ensuring that any handling, storage, or sharing of these documents complies with GDPR
								regulations, particularly in regards to personal data protection.
							</Note>
						</>
					) : (
						renderFallback()
					)}
				</>
			)}
			{confirmDeleteModalOpen && (
				<Modal
					isOpen={confirmDeleteModalOpen}
					onRequestClose={() => setConfirmDeleteModalOpen(false)}
					style={ModalStyles}
					contentLabel="Confirm Delete Page"
					shouldCloseOnOverlayClick={true}
					ariaHideApp={false}>
					<ConfirmModal
						title={`Are you sure you want to delete page ${currentPage + 1}?`}
						content="This action cannot be undone. The file will be permanently removed from this document."
						confirmButtonText="Yes, Delete Page"
						cancelButtonText="Cancel"
						confirmButtonAction={deleteCurrentPage}
						cancelButtonAction={() => setConfirmDeleteModalOpen(false)}
						external={false}
					/>
				</Modal>
			)}
		</PreviewWrapper>
	);
};

const mapDispatchToProps = {
	toggleLoading,
	setAlert,
};

export default connect(null, mapDispatchToProps)(memo(FilePreview));

const validFileTypes = ["gif", "jpg", "jpeg", "JPEG", "PNG", "pdf", "png", "tiff"];
